import React, { useState, useCallback } from 'react';
import { db } from './firebase';
import { collection, getDocs, deleteDoc, writeBatch, doc } from 'firebase/firestore';

const CLIENTS = [
  { id: '2ejjeh', label: '2ejjeh' },
  { id: 'lucea', label: 'lucea' }
];

const DATA_TYPES = [
  { id: 'imports', label: 'Import Data', description: 'All imported Excel data and items' },
  { id: 'paid', label: 'Paid Items', description: 'Items marked as paid/delivered' },
  { id: 'returned', label: 'Returned Items', description: 'Items marked as returned' }
];

function AdminDataManagementPage({ userRole, userClientId }) {
  const [selectedClient, setSelectedClient] = useState(userClientId || '2ejjeh');
  const [selectedDataType, setSelectedDataType] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [progressText, setProgressText] = useState('');
  const [errorText, setErrorText] = useState('');
  const [deleteSummary, setDeleteSummary] = useState(null);
  const [dataPreview, setDataPreview] = useState(null);
  const [batchSize, setBatchSize] = useState(200);
  const [throttleMs, setThrottleMs] = useState(250);

  // Preview data before deletion
  const handlePreviewData = useCallback(async () => {
    if (!selectedClient || !selectedDataType) {
      setErrorText('Please select both client and data type');
      return;
    }

    setProgressText('Loading data preview...');
    setErrorText('');
    setDataPreview(null);

    try {
      let totalDocuments = 0;
      let totalItems = 0;
      const previewData = {
        clientId: selectedClient,
        dataType: selectedDataType,
        collections: {}
      };

      if (selectedDataType === 'imports') {
        // Get all import documents and their items
        const importsRef = collection(db, 'clients', selectedClient, 'imports');
        const importsSnapshot = await getDocs(importsRef);
        
        previewData.collections.imports = [];
        totalDocuments = importsSnapshot.docs.length;

        for (const importDoc of importsSnapshot.docs) {
          const itemsRef = collection(db, 'clients', selectedClient, 'imports', importDoc.id, 'items');
          const itemsSnapshot = await getDocs(itemsRef);
          
          previewData.collections.imports.push({
            importId: importDoc.id,
            importData: importDoc.data(),
            itemCount: itemsSnapshot.docs.length
          });
          
          totalItems += itemsSnapshot.docs.length;
        }
      } else {
        // For paid and returned collections
        const collectionRef = collection(db, 'clients', selectedClient, selectedDataType);
        const snapshot = await getDocs(collectionRef);
        
        previewData.collections[selectedDataType] = snapshot.docs.map(doc => ({
          id: doc.id,
          data: doc.data()
        }));
        
        totalDocuments = snapshot.docs.length;
        totalItems = totalDocuments;
      }

      setDataPreview({
        ...previewData,
        totalDocuments,
        totalItems
      });
      setProgressText('Data preview loaded successfully');
    } catch (error) {
      console.error('Error previewing data:', error);
      setErrorText(error?.message || 'Failed to load data preview');
      setProgressText('');
    }
  }, [selectedClient, selectedDataType]);

  // Delete data function
  const handleDeleteData = useCallback(async () => {
    if (!selectedClient || !selectedDataType) {
      setErrorText('Please select both client and data type');
      return;
    }

    const confirmMessage = `Are you sure you want to delete ALL ${selectedDataType} data for client "${selectedClient}"?\n\nThis action cannot be undone!`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsDeleting(true);
    setProgressText('Starting deletion...');
    setErrorText('');
    setDeleteSummary(null);

    try {
      let totalDeleted = 0;
      let batch = writeBatch(db);
      let opsInBatch = 0;

      if (selectedDataType === 'imports') {
        // Delete imports and their items
        const importsRef = collection(db, 'clients', selectedClient, 'imports');
        const importsSnapshot = await getDocs(importsRef);
        
        for (const importDoc of importsSnapshot.docs) {
          setProgressText(`Deleting import ${importDoc.id}...`);
          
          // Delete all items in this import
          const itemsRef = collection(db, 'clients', selectedClient, 'imports', importDoc.id, 'items');
          const itemsSnapshot = await getDocs(itemsRef);
          
          for (const itemDoc of itemsSnapshot.docs) {
            batch.delete(itemDoc.ref);
            opsInBatch++;
            totalDeleted++;

            if (opsInBatch >= batchSize) {
              await batch.commit();
              if (throttleMs > 0) {
                await new Promise(res => setTimeout(res, throttleMs));
              }
              batch = writeBatch(db);
              opsInBatch = 0;
            }
          }

          // Delete the import document itself
          batch.delete(importDoc.ref);
          opsInBatch++;
          totalDeleted++;

          if (opsInBatch >= batchSize) {
            await batch.commit();
            if (throttleMs > 0) {
              await new Promise(res => setTimeout(res, throttleMs));
            }
            batch = writeBatch(db);
            opsInBatch = 0;
          }
        }
      } else {
        // Delete paid or returned items
        const collectionRef = collection(db, 'clients', selectedClient, selectedDataType);
        const snapshot = await getDocs(collectionRef);
        
        for (const docSnapshot of snapshot.docs) {
          setProgressText(`Deleting ${selectedDataType} item ${docSnapshot.id}...`);
          
          batch.delete(docSnapshot.ref);
          opsInBatch++;
          totalDeleted++;

          if (opsInBatch >= batchSize) {
            await batch.commit();
            if (throttleMs > 0) {
              await new Promise(res => setTimeout(res, throttleMs));
            }
            batch = writeBatch(db);
            opsInBatch = 0;
          }
        }
      }

      // Commit any remaining operations
      if (opsInBatch > 0) {
        await batch.commit();
      }

      setDeleteSummary({
        clientId: selectedClient,
        dataType: selectedDataType,
        totalDeleted,
        deletedAt: new Date().toISOString()
      });
      
      setProgressText(`Successfully deleted ${totalDeleted} ${selectedDataType} records for client ${selectedClient}`);
      setDataPreview(null);
    } catch (error) {
      console.error('Error deleting data:', error);
      setErrorText(error?.message || 'Failed to delete data');
      setProgressText('');
    } finally {
      setIsDeleting(false);
    }
  }, [selectedClient, selectedDataType, batchSize, throttleMs]);

  // Delete all data for a client
  const handleDeleteAllClientData = useCallback(async () => {
    if (!selectedClient) {
      setErrorText('Please select a client');
      return;
    }

    const confirmMessage = `⚠️ DANGER: Are you sure you want to delete ALL data for client "${selectedClient}"?\n\nThis will delete:\n- All import data and items\n- All paid items\n- All returned items\n\nThis action cannot be undone!`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsDeleting(true);
    setProgressText('Starting complete client data deletion...');
    setErrorText('');
    setDeleteSummary(null);

    try {
      let totalDeleted = 0;
      let batch = writeBatch(db);
      let opsInBatch = 0;

      // Delete all data types
      for (const dataType of DATA_TYPES) {
        setProgressText(`Deleting ${dataType.label} for ${selectedClient}...`);
        
        if (dataType.id === 'imports') {
          // Delete imports and their items
          const importsRef = collection(db, 'clients', selectedClient, 'imports');
          const importsSnapshot = await getDocs(importsRef);
          
          for (const importDoc of importsSnapshot.docs) {
            const itemsRef = collection(db, 'clients', selectedClient, 'imports', importDoc.id, 'items');
            const itemsSnapshot = await getDocs(itemsRef);
            
            for (const itemDoc of itemsSnapshot.docs) {
              batch.delete(itemDoc.ref);
              opsInBatch++;
              totalDeleted++;

              if (opsInBatch >= batchSize) {
                await batch.commit();
                if (throttleMs > 0) {
                  await new Promise(res => setTimeout(res, throttleMs));
                }
                batch = writeBatch(db);
                opsInBatch = 0;
              }
            }

            batch.delete(importDoc.ref);
            opsInBatch++;
            totalDeleted++;

            if (opsInBatch >= batchSize) {
              await batch.commit();
              if (throttleMs > 0) {
                await new Promise(res => setTimeout(res, throttleMs));
              }
              batch = writeBatch(db);
              opsInBatch = 0;
            }
          }
        } else {
          // Delete paid or returned items
          const collectionRef = collection(db, 'clients', selectedClient, dataType.id);
          const snapshot = await getDocs(collectionRef);
          
          for (const docSnapshot of snapshot.docs) {
            batch.delete(docSnapshot.ref);
            opsInBatch++;
            totalDeleted++;

            if (opsInBatch >= batchSize) {
              await batch.commit();
              if (throttleMs > 0) {
                await new Promise(res => setTimeout(res, throttleMs));
              }
              batch = writeBatch(db);
              opsInBatch = 0;
            }
          }
        }
      }

      // Commit any remaining operations
      if (opsInBatch > 0) {
        await batch.commit();
      }

      setDeleteSummary({
        clientId: selectedClient,
        dataType: 'all',
        totalDeleted,
        deletedAt: new Date().toISOString()
      });
      
      setProgressText(`Successfully deleted ${totalDeleted} total records for client ${selectedClient}`);
      setDataPreview(null);
    } catch (error) {
      console.error('Error deleting all client data:', error);
      setErrorText(error?.message || 'Failed to delete all client data');
      setProgressText('');
    } finally {
      setIsDeleting(false);
    }
  }, [selectedClient, batchSize, throttleMs]);

  return (
    <div style={{ 
      padding: '32px', 
      maxWidth: '1200px', 
      margin: '0 auto',
      backgroundColor: '#f8f9fa',
      minHeight: '100vh'
    }}>
      {/* Header Section */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '32px',
        marginBottom: '24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px' }}>
          <div style={{
            width: '48px',
            height: '48px',
            backgroundColor: '#dc3545',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '16px'
          }}>
            <span style={{ color: 'white', fontSize: '24px' }}>🗑️</span>
          </div>
          <div>
            <h1 style={{ 
              margin: 0, 
              fontSize: '28px', 
              color: '#2c3e50',
              fontWeight: '600'
            }}>
              Admin Data Management
            </h1>
            <p style={{ 
              margin: '4px 0 0 0', 
              color: '#6c757d', 
              fontSize: '16px' 
            }}>
              Manage and delete client data (Admin Only)
            </p>
          </div>
        </div>

        {/* Client Selection */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{
            display: 'block',
            marginBottom: '8px',
            color: '#495057',
            fontWeight: '600',
            fontSize: '16px'
          }}>
            Select Client
          </label>
          <select
            value={selectedClient}
            onChange={(e) => setSelectedClient(e.target.value)}
            disabled={isDeleting}
            style={{
              padding: '12px 16px',
              border: '2px solid #e9ecef',
              borderRadius: '8px',
              fontSize: '16px',
              backgroundColor: 'white',
              color: '#495057',
              minWidth: '200px',
              cursor: 'pointer'
            }}
          >
            {CLIENTS.map(c => (
              <option key={c.id} value={c.id}>{c.label}</option>
            ))}
          </select>
        </div>

        {/* Data Type Selection */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{
            display: 'block',
            marginBottom: '8px',
            color: '#495057',
            fontWeight: '600',
            fontSize: '16px'
          }}>
            Select Data Type to Manage
          </label>
          <select
            value={selectedDataType}
            onChange={(e) => setSelectedDataType(e.target.value)}
            disabled={isDeleting}
            style={{
              padding: '12px 16px',
              border: '2px solid #e9ecef',
              borderRadius: '8px',
              fontSize: '16px',
              backgroundColor: 'white',
              color: '#495057',
              minWidth: '300px',
              cursor: 'pointer'
            }}
          >
            <option value="">Select data type...</option>
            {DATA_TYPES.map(dt => (
              <option key={dt.id} value={dt.id}>{dt.label} - {dt.description}</option>
            ))}
          </select>
        </div>

        {/* Advanced Settings */}
        <div style={{
          backgroundColor: '#f8f9fa',
          padding: '20px',
          borderRadius: '8px',
          marginBottom: '24px'
        }}>
          <h3 style={{ 
            margin: '0 0 16px 0', 
            fontSize: '18px', 
            color: '#495057',
            fontWeight: '600'
          }}>
            ⚙️ Advanced Settings
          </h3>
          <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
            <div>
              <label style={{
                display: 'block',
                marginBottom: '6px',
                color: '#6c757d',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                Batch Size
              </label>
              <input
                type="number"
                min={50}
                max={450}
                step={50}
                value={batchSize}
                onChange={(e) => setBatchSize(Math.max(50, Math.min(450, Number(e.target.value) || 200)))}
                disabled={isDeleting}
                style={{
                  width: '120px',
                  padding: '8px 12px',
                  border: '1px solid #ced4da',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>
            <div>
              <label style={{
                display: 'block',
                marginBottom: '6px',
                color: '#6c757d',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                Throttle Delay
              </label>
              <input
                type="number"
                min={0}
                step={50}
                value={throttleMs}
                onChange={(e) => setThrottleMs(Math.max(0, Number(e.target.value) || 0))}
                disabled={isDeleting}
                style={{
                  width: '120px',
                  padding: '8px 12px',
                  border: '1px solid #ced4da',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '32px',
        marginBottom: '24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{ 
          margin: '0 0 24px 0', 
          fontSize: '22px', 
          color: '#2c3e50',
          fontWeight: '600'
        }}>
          🎯 Data Management Actions
        </h2>

        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '24px' }}>
          <button 
            onClick={handlePreviewData} 
            disabled={!selectedClient || !selectedDataType || isDeleting}
            style={{
              backgroundColor: (!selectedClient || !selectedDataType || isDeleting) ? '#6c757d' : '#17a2b8',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: (!selectedClient || !selectedDataType || isDeleting) ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            👁️ Preview Data
          </button>

          <button 
            onClick={handleDeleteData} 
            disabled={!selectedClient || !selectedDataType || isDeleting}
            style={{
              backgroundColor: (!selectedClient || !selectedDataType || isDeleting) ? '#6c757d' : '#dc3545',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: (!selectedClient || !selectedDataType || isDeleting) ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            🗑️ Delete Selected Data
          </button>
        </div>

        <div style={{ 
          borderTop: '2px solid #e9ecef', 
          paddingTop: '24px',
          marginTop: '24px'
        }}>
          <h3 style={{ 
            margin: '0 0 16px 0', 
            fontSize: '18px', 
            color: '#dc3545',
            fontWeight: '600'
          }}>
            ⚠️ DANGER ZONE
          </h3>
          <p style={{ 
            margin: '0 0 16px 0', 
            color: '#6c757d', 
            fontSize: '14px' 
          }}>
            This will delete ALL data for the selected client (imports, paid, returned).
          </p>
          <button 
            onClick={handleDeleteAllClientData} 
            disabled={!selectedClient || isDeleting}
            style={{
              backgroundColor: (!selectedClient || isDeleting) ? '#6c757d' : '#dc3545',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: (!selectedClient || isDeleting) ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            💥 Delete ALL Client Data
          </button>
        </div>
      </div>

      {/* Progress Section */}
      {progressText && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ 
            margin: '0 0 16px 0', 
            fontSize: '18px', 
            color: '#495057',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            ⏳ Progress
          </h3>
          <div style={{
            backgroundColor: '#f8f9fa',
            padding: '16px',
            borderRadius: '8px',
            fontFamily: 'monospace',
            fontSize: '14px',
            color: '#495057',
            whiteSpace: 'pre-wrap',
            border: '1px solid #e9ecef'
          }}>
            {progressText}
          </div>
        </div>
      )}

      {/* Error Section */}
      {errorText && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          border: '1px solid #f5c6cb'
        }}>
          <h3 style={{ 
            margin: '0 0 16px 0', 
            fontSize: '18px', 
            color: '#721c24',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            ❌ Error
          </h3>
          <div style={{
            backgroundColor: '#f8d7da',
            padding: '16px',
            borderRadius: '8px',
            fontFamily: 'monospace',
            fontSize: '14px',
            color: '#721c24',
            whiteSpace: 'pre-wrap',
            border: '1px solid #f5c6cb'
          }}>
            {errorText}
          </div>
        </div>
      )}

      {/* Data Preview Section */}
      {dataPreview && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          border: '1px solid #c3e6cb'
        }}>
          <h3 style={{ 
            margin: '0 0 20px 0', 
            fontSize: '18px', 
            color: '#155724',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            👁️ Data Preview
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '20px' }}>
            <div style={{
              backgroundColor: '#d4edda',
              padding: '16px',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#155724' }}>
                {dataPreview.totalDocuments}
              </div>
              <div style={{ fontSize: '14px', color: '#155724' }}>
                Documents
              </div>
            </div>
            <div style={{
              backgroundColor: '#d4edda',
              padding: '16px',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#155724' }}>
                {dataPreview.totalItems}
              </div>
              <div style={{ fontSize: '14px', color: '#155724' }}>
                Total Items
              </div>
            </div>
          </div>
          <details>
            <summary style={{ cursor: 'pointer', fontWeight: '600', marginBottom: '12px' }}>
              View Detailed Preview
            </summary>
            <pre style={{ 
              background: '#f8f9fa', 
              padding: '16px', 
              borderRadius: '8px',
              fontSize: '12px',
              overflow: 'auto',
              maxHeight: '300px',
              border: '1px solid #e9ecef'
            }}>
              {JSON.stringify(dataPreview, null, 2)}
            </pre>
          </details>
        </div>
      )}

      {/* Delete Summary Section */}
      {deleteSummary && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          border: '1px solid #c3e6cb'
        }}>
          <h3 style={{ 
            margin: '0 0 20px 0', 
            fontSize: '18px', 
            color: '#155724',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            ✅ Deletion Summary
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
            <div style={{
              backgroundColor: '#d4edda',
              padding: '16px',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#155724' }}>
                {deleteSummary.totalDeleted}
              </div>
              <div style={{ fontSize: '14px', color: '#155724' }}>
                Records Deleted
              </div>
            </div>
            <div style={{
              backgroundColor: '#d4edda',
              padding: '16px',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#155724' }}>
                {deleteSummary.clientId}
              </div>
              <div style={{ fontSize: '14px', color: '#155724' }}>
                Client
              </div>
            </div>
            <div style={{
              backgroundColor: '#d4edda',
              padding: '16px',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#155724' }}>
                {deleteSummary.dataType}
              </div>
              <div style={{ fontSize: '14px', color: '#155724' }}>
                Data Type
              </div>
            </div>
          </div>
          <div style={{ marginTop: '16px', fontSize: '14px', color: '#6c757d' }}>
            Deleted at: {new Date(deleteSummary.deletedAt).toLocaleString()}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDataManagementPage;



